import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useHero } from "../../hooks/useHomeContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absolutize = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

/**
 * Full-screen auto-playing video hero. Fills the viewport (including under the
 * fixed navbar), text sits bottom-left, no product photos. Admin-editable via
 * Homepage > Hero (fields: heroVideo, heroVideoPoster + all copy). The media
 * layer drifts on scroll to give the hero a subtle parallax feel.
 */
export default function HeroBanner() {
  const hero = useHero();
  const video = absolutize(hero.heroVideo);
  const poster = absolutize(hero.heroVideoPoster);
  const sectionRef = useRef(null);

  // Parallax — write --parallax-y (px) on the section as the user scrolls the
  // first viewport. Keeps everything CSS-driven so it stays 60 fps.
  useEffect(() => {
    let raf = 0;
    let latestY = 0;
    const onScroll = () => {
      latestY = window.scrollY;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        if (sectionRef.current) {
          const clamped = Math.min(latestY, window.innerHeight);
          sectionRef.current.style.setProperty("--parallax-y", `${clamped}px`);
        }
        raf = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="hb-full" id="top" data-testid="hero-fullscreen" ref={sectionRef}>
      <div className="hb-full-media" aria-hidden="true">
        {video ? (
          <video
            src={video}
            poster={poster || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : (
          poster && <img src={poster} alt="" />
        )}
        <div className="hb-full-scrim" />
      </div>

      <motion.div
        className="hb-full-copy"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.span
          className="hb-full-eyebrow"
          initial={{ opacity: 0, letterSpacing: "0.05em" }}
          animate={{ opacity: 1, letterSpacing: "0.28em" }}
          transition={{ duration: 1.1, delay: 0.2 }}
        >
          <Sparkles size={11} /> {hero.eyebrow}
        </motion.span>

        <h1 className="hb-full-title" data-testid="hero-title">
          <motion.span
            className="hb-full-title-line"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
          >
            {hero.titleLine1}
          </motion.span>
          <motion.span
            className="hb-full-title-line hb-full-shine"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
          >
            {hero.titleLine2}
          </motion.span>
        </h1>

        <motion.p
          className="hb-full-lead"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.65 }}
        >
          {hero.lead}
        </motion.p>

        <motion.div
          className="hb-full-ctas"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
        >
          <Link
            to={hero.ctaPrimaryLink}
            className="hb-full-cta hb-full-cta-primary"
            data-testid="hero-cta-primary"
          >
            {hero.ctaPrimaryLabel} <ArrowRight size={16} />
          </Link>
          <Link
            to={hero.ctaSecondaryLink}
            className="hb-full-cta hb-full-cta-ghost"
            data-testid="hero-cta-secondary"
          >
            {hero.ctaSecondaryLabel}
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}
